import React, { useState } from "react";
import "./App.css";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Analytics } from "@vercel/analytics/react";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatICSDateUTC(date) {
  // All-day event date format: YYYYMMDD
  return `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}`;
}

function escapeICSText(text) {
  // RFC5545 escaping for text fields
  return String(text)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
}

function buildICS({ rows, dob, country }) {
  const created = new Date();

  const header = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "PRODID:-//Kids Vaccination Schedule//EN"
  ];

  const events = rows
    .filter((r) => r && r.date && r.offset !== null && r.offset !== undefined)
    .map((r, idx) => {
      const due = new Date(r.date);
      if (Number.isNaN(due.getTime())) return null;

      // All-day events use DTSTART;VALUE=DATE and DTEND;VALUE=DATE (end is next day)
      const dtStart = formatICSDateUTC(due);
      const dtEndDate = new Date(due);
      dtEndDate.setUTCDate(dtEndDate.getUTCDate() + 1);
      const dtEnd = formatICSDateUTC(dtEndDate);

      const uid = `vax-${escapeICSText(country)}-${escapeICSText(dob)}-${idx}@kids-vax`;
      const summary = `Vaccine due: ${r.vaccine}`;
      const description = `Age: ${r.age} | Country: ${country} | DOB: ${dob}`;

      return [
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${formatICSDateUTC(created)}T${pad2(created.getUTCHours())}${pad2(created.getUTCMinutes())}${pad2(created.getUTCSeconds())}Z`,
        `SUMMARY:${escapeICSText(summary)}`,
        `DESCRIPTION:${escapeICSText(description)}`,
        `DTSTART;VALUE=DATE:${dtStart}`,
        `DTEND;VALUE=DATE:${dtEnd}`,
        "END:VEVENT"
      ].join("\r\n");
    })
    .filter(Boolean);

  return [...header, ...events, "END:VCALENDAR"].join("\r\n");
}

function downloadTextFile({ filename, text, mimeType }) {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

const schedules = {
  India: [
    {
      age: "At Birth",
      vaccine: "BCG, OPV-0, Hepatitis B (Birth dose)",
      offset: 0
    },

    {
      age: "6 Weeks",
      vaccine: "Pentavalent-1 (DTP + HepB + Hib), OPV-1, Rotavirus-1, fIPV-1, PCV-1*",
      offset: 42
    },

    {
      age: "10 Weeks",
      vaccine: "Pentavalent-2, OPV-2, Rotavirus-2",
      offset: 70
    },

    {
      age: "14 Weeks",
      vaccine: "Pentavalent-3, OPV-3, Rotavirus-3, fIPV-2, PCV-2*",
      offset: 98
    },

    {
      age: "9 Months",
      vaccine: "MR-1 (Measles-Rubella), JE-1**, PCV Booster*",
      offset: 270
    },

    {
      age: "16–24 Months",
      vaccine: "MR-2, JE-2**, DPT Booster-1, OPV Booster",
      offset: 480
    },

    {
      age: "5–6 Years",
      vaccine: "DPT Booster-2",
      offset: 1825
    },

    {
      age: "10 Years",
      vaccine: "Td (Tetanus & adult Diphtheria)",
      offset: 3650
    },

    {
      age: "16 Years",
      vaccine: "Td Booster",
      offset: 5840
    }
  ],
  USA: [
    { age: "At Birth", vaccine: "Hepatitis B (1st dose)", offset: 0 },

    { age: "2 Months", vaccine: "DTaP (1st dose), IPV (1st dose), Hib (1st dose), PCV (1st dose), Rotavirus (1st dose)", offset: 60 },

    { age: "4 Months", vaccine: "DTaP (2nd dose), IPV (2nd dose), Hib (2nd dose), PCV (2nd dose), Rotavirus (2nd dose)", offset: 120 },

    { age: "6 Months", vaccine: "DTaP (3rd dose), IPV (3rd dose), PCV (3rd dose), Influenza (annual)**", offset: 180 },

    { age: "12 Months", vaccine: "MMR (1st dose), Varicella (1st dose), Hepatitis A (1st dose), Hib (booster), PCV (booster)", offset: 365 },

    { age: "15–18 Months", vaccine: "DTaP (4th dose)", offset: 540 },

    { age: "4–6 Years", vaccine: "DTaP (5th dose), IPV (4th dose), Varicella (2nd dose), MMR (2nd dose)", offset: 1825 },

    { age: "11–12 Years", vaccine: "Tdap (booster), HPV (start series), Meningococcal (1st)", offset: 4380 },

    { age: "16 Years", vaccine: "Meningococcal (2nd dose)", offset: 5840 }
  ],
  UK: [
       {
      age: "8 Weeks",
      vaccine: "6-in-1 (1st), Rotavirus (1st), MenB (1st)",
      offset: 56
    },
    {
      age: "12 Weeks",
      vaccine: "6-in-1 (2nd), Rotavirus (2nd), MenB (2nd)",
      offset: 84
    },
    {
      age: "16 Weeks",
      vaccine: "6-in-1 (3rd), Pneumococcal (PCV1)",
      offset: 112
    },
    {
      age: "1 Year",
      vaccine: "MMRV (1st), Pneumococcal (PCV2), MenB (3rd)",
      offset: 365
    },
    {
      age: "18 Months",
      vaccine: "6-in-1 (4th, for recent cohorts), MMRV (2nd)",
      offset: 547
    },
    {
      age: "3 Years 4 Months",
      vaccine: "Pre-school 4-in-1 booster",
      offset: 1244
    },
    {
      age: "12–13 Years",
      vaccine: "HPV vaccine (start)",
      offset: 4380
    },
    {
      age: "14 Years",
      vaccine: "Td/IPV teenage booster, MenACWY",
      offset: 5110
    },
    {
      age: "Annual (2–15 yrs)",
      vaccine: "Seasonal flu (yearly until Year 11)",
      offset: null
    }
  ],
    Canada: [
    {
      age: "2 Months",
      vaccine: "DTaP-IPV-Hib (or DTaP-HB-IPV-Hib), Pneumococcal Conjugate, Rotavirus",
      offset: 60
    },
    {
      age: "4 Months",
      vaccine: "DTaP-IPV-Hib (or DTaP-HB-IPV-Hib), Pneumococcal Conjugate, Rotavirus",
      offset: 120
    },
    {
      age: "6 Months",
      vaccine: "DTaP-IPV-Hib (or DTaP-HB-IPV-Hib), Pneumococcal Conjugate",
      offset: 180
    },
    {
      age: "12 Months",
      vaccine: "Measles-Mumps-Rubella (MMR), Pneumococcal Conjugate, Meningococcal C Conjugate, Varicella",
      offset: 365
    },
    {
      age: "15 Months",
      vaccine: "DTaP-IPV-Hib (or DTaP-HB-IPV-Hib)",
      offset: 450
    },
    {
      age: "18 Months",
      vaccine: "Measles-Mumps-Rubella (2nd dose), Varicella (2nd dose)",
      offset: 540
    },
    {
      age: "4–6 Years",
      vaccine: "DTaP-IPV (4–6 year booster), Measles-Mumps-Rubella-Varicella (MMRV)†",
      offset: 1825
    },
    {
      age: "9 Years",
      vaccine: "Human Papillomavirus (HPV) (often first dose for ages 9–17)",
      offset: 3285
    }
  ],
    Australia: [
    {
      age: "Birth",
      vaccine: "Hepatitis B (within 7 days)",
      offset: 0
    },
    {
      age: "2 Months",
      vaccine: "Diphtheria/Tetanus/Pertussis/HepB/Polio/Hib, Rotavirus (1st), Pneumococcal, Meningococcal B*",
      offset: 60
    },
    {
      age: "4 Months",
      vaccine: "Diphtheria/Tetanus/Pertussis/HepB/Polio/Hib, Rotavirus (2nd), Pneumococcal, Meningococcal B*",
      offset: 120
    },
    {
      age: "6 Months",
      vaccine: "Diphtheria/Tetanus/Pertussis/HepB/Polio/Hib, Pneumococcal*, Meningococcal B*",
      offset: 180
    },
    {
      age: "6 Months – <5 Years",
      vaccine: "Influenza (annual)",
      offset: 180
    },
    {
      age: "12 Months",
      vaccine: "Meningococcal ACWY, Measles/Mumps/Rubella (MMR), Pneumococcal, Meningococcal B*",
      offset: 365
    },
    {
      age: "18 Months",
      vaccine: "Haemophilus influenzae type b (Hib), Measles/Mumps/Rubella/Varicella, Diphtheria/Tetanus/Pertussis",
      offset: 540
    },
    {
      age: "4 Years",
      vaccine: "Diphtheria/Tetanus/Pertussis/Polio",
      offset: 1460
    },
    {
      age: "12–13 Years",
      vaccine: "Human Papillomavirus (HPV), Diphtheria/Tetanus/Pertussis (adolescent booster)",
      offset: 4380
    },
    {
      age: "14–16 Years",
      vaccine: "Meningococcal ACWY (adolescent)",
      offset: 5110
    }
  ]
};

const COUNTRY_SOURCES = {
  India: {
    label: "Ministry of Health & Family Welfare (MoHFW)",
    url: "https://www.mohfw.gov.in/sites/default/files/245453521061489663873.pdf"
  },
  USA: {
    label: "Centers for Disease Control and Prevention (CDC)",
    url: "https://www.cdc.gov/vaccines/hcp/imz-schedules/child-adolescent-age.html"
  },
  UK: {
    label: "National Health Service (NHS)",
    url: "https://www.nhs.uk/vaccinations/nhs-vaccinations-and-when-to-have-them/"
  },
  Canada: {
    label: "Government of Canada immunization guide",
    url: "https://www.canada.ca/en/public-health/services/publications/healthy-living/canadian-immunization-guide-part-1-key-immunization-information/page-13-recommended-immunization-schedules.html"
  },
  Australia: {
    label: "Australia — National Immunisation Program",
    url: "https://www.health.gov.au/sites/default/files/2026-01/national-immunisation-program-schedule.pdf"
  }
};

export default function App() {
  const [dob, setDob] = useState("");
  const [country, setCountry] = useState("");
  const [rows, setRows] = useState([]);

  // Snapshot of inputs used for the last generated table.
  // This prevents the table/source from changing until the user clicks "View Schedule" again.
  const [scheduleDob, setScheduleDob] = useState("");
  const [scheduleCountry, setScheduleCountry] = useState("");

  const path = typeof window !== "undefined" ? window.location.pathname : "/";

  const generateSchedule = () => {
    if (!dob || !country) return;
    const base = new Date(dob);
    const data = schedules[country].map(item => {
      // Some schedules may contain entries without a fixed due date (e.g., "Annual").
      if (item.offset === null || item.offset === undefined) {
        return {
          ...item,
          date: ""
        };
      }
      const due = new Date(base);
      due.setDate(due.getDate() + item.offset);
      return {
        ...item,
        date: due.toDateString()
      };
    });
    setRows(data);

    // Update snapshot only when schedule is generated.
    setScheduleDob(dob);
    setScheduleCountry(country);
  };

  const downloadICS = () => {
    // Use the last generated schedule snapshot so exports don't change until user clicks "View Schedule" again.
    if (!scheduleDob || !scheduleCountry || rows.length === 0) return;

    const icsText = buildICS({ rows, dob: scheduleDob, country: scheduleCountry });
    const filename = `vaccination-schedule-${scheduleCountry}-${scheduleDob}.ics`;

    downloadTextFile({
      filename,
      text: icsText,
      mimeType: "text/calendar;charset=utf-8"
    });
  };

  const exportVaccinationCardPdf = () => {
    // Use the last generated schedule snapshot so exports don't change until user clicks "View Schedule" again.
    if (!scheduleDob || !scheduleCountry || rows.length === 0) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4"
    });

    // Title
    doc.setFontSize(18);
    doc.text("Vaccination Card", 40, 50);

    doc.setFontSize(11);
    doc.setTextColor(90);
    doc.text(`Country: ${scheduleCountry}`, 40, 72);
    doc.text(`Date of Birth: ${scheduleDob}`, 40, 88);

    // Table rows: keep the same 3 columns shown in UI
    const body = rows.map((r) => [r.age || "", r.vaccine || "", r.date || ""]);

    autoTable(doc, {
      startY: 110,
      head: [["Age", "Vaccine", "Due Date"]],
      body,
      styles: {
        fontSize: 10,
        cellPadding: 6,
        overflow: "linebreak"
      },
      headStyles: {
        fillColor: [234, 247, 246],
        textColor: [31, 41, 55]
      },
      theme: "grid",
      columnStyles: {
        0: { cellWidth: 110 },
        1: { cellWidth: 300 },
        2: { cellWidth: 110 }
      }
    });

    const filename = `vaccination-card-${scheduleCountry}-${scheduleDob}.pdf`;
    doc.save(filename);
  };

  /**
   * TODO (Google Calendar export)
   *
   * Later, if you want to let users add events directly into Google Calendar,
   * you have two approaches:
   *
   * 1) Simple (no OAuth, easiest): open Google Calendar's "TEMPLATE" URL.
   *    - Limitation: it only creates ONE event per URL.
   *    - For "export all" you'd need multiple links/tabs (poor UX).
   *
   *    Example URL:
   *    https://calendar.google.com/calendar/render?action=TEMPLATE&text=...&details=...&dates=START/END
   *
   * 2) Full export-all (OAuth): use Google Calendar API.
   *    - Requires OAuth consent + access token.
   *    - Then call Calendar API to create/insert events programmatically.
   *    - Best if you truly want "export all" in one click.
   */

  // Example helper for Approach 1 (single-event URL). Not enabled right now.
  // function buildGoogleCalendarTemplateUrl({ title, details, startDate, endDate }) {
  //   // All-day event format: YYYYMMDD/YYYYMMDD (end date is next day)
  //   const fmt = (d) => {
  //     const yyyy = d.getFullYear();
  //     const mm = String(d.getMonth() + 1).padStart(2, "0");
  //     const dd = String(d.getDate()).padStart(2, "0");
  //     return `${yyyy}${mm}${dd}`;
  //   };
  //
  //   const params = new URLSearchParams({
  //     action: "TEMPLATE",
  //     text: title,
  //     details,
  //     dates: `${fmt(startDate)}/${fmt(endDate)}`
  //   });
  //
  //   return `https://calendar.google.com/calendar/render?${params.toString()}`;
  // }
  //
  // function exportNextDoseToGoogleCalendar() {
  //   // Recommended UX if using Approach 1: export ONLY the next/first dose.
  //   if (!dob || !country || rows.length === 0) return;
  //   const first = rows.find((r) => r.date);
  //   if (!first) return;
  //
  //   const start = new Date(first.date);
  //   const end = new Date(start);
  //   end.setDate(end.getDate() + 1);
  //
  //   const url = buildGoogleCalendarTemplateUrl({
  //     title: `Vaccine due: ${first.vaccine}`,
  //     details: `Age: ${first.age} | Country: ${country} | DOB: ${dob}`,
  //     startDate: start,
  //     endDate: end
  //   });
  //
  //   window.open(url, "_blank", "noopener,noreferrer");
  // }

  // Simple Disclaimer page without adding a routing library.
  // NOTE: On static hosting you may need a redirect rule so /disclaimer serves index.html.
  if (path === "/disclaimer") {
    return (
      <div className="container">
        <header>
          <h1>Disclaimer</h1>
          <p>
            This app is for informational purposes only and does not replace professional medical advice. Please consult
            your pediatrician for vaccination decisions.
          </p>
        </header>

        <div className="card">
          <p>
            Vaccination schedules may vary by country, region, medical history, and updates from public health
            authorities.
          </p>
          <p>
            <a href="/">← Back to schedule</a>
          </p>
        </div>

        <footer className="footer">
          <a href="/">Home</a>
        </footer>
      </div>
    );
  }

  return (
    <div className="container">
      <header>
        <h1>Vaccination Schedule Calculator for Kids</h1>
        <p>Enter your child’s date of birth and country to get exact vaccine due dates.</p>
      </header>

      <div className="card input-card">
        <div className="field">
          <label>Date of Birth</label>
          <input type="date" value={dob} onChange={e => setDob(e.target.value)} />
        </div>

        <div className="field">
          <label>Country</label>
          <select value={country} onChange={e => setCountry(e.target.value)}>
            <option value="">Select country</option>
            {Object.keys(schedules).map(c => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>

        <button onClick={generateSchedule}>View Schedule</button>
      </div>

      {rows.length > 0 && (
        <div className="card">
          <h2>Vaccination Schedule</h2>
          <table>
            <thead>
              <tr>
                <th>Age</th>
                <th>Vaccine</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.age}</td>
                  <td>{r.vaccine}</td>
                  <td>{r.date}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {scheduleCountry && COUNTRY_SOURCES[scheduleCountry] && (
            <div className="source">
              <span className="sourceTitle">ℹ️ Source:</span> {scheduleCountry} –{" "}
              <a
                className="sourceLink"
                href={COUNTRY_SOURCES[scheduleCountry].url}
                target="_blank"
                rel="noreferrer"
              >
                {COUNTRY_SOURCES[scheduleCountry].label}
              </a>
            </div>
          )}

          <div className="actions">
            {/*
              Google Calendar export is intentionally disabled for now.

              When you want to enable it later, decide:
              - Approach 1 (URL template): good for exporting a single event (next dose)
              - Approach 2 (OAuth + API): required for exporting all events in one click

              Then add an onClick handler, e.g.:
              onClick={exportNextDoseToGoogleCalendar}
            */}
            <button className="secondary" onClick={downloadICS}>
              Download .ICS
            </button>
            <button className="secondary" onClick={exportVaccinationCardPdf}>
              Export Vaccination Card
            </button>
          </div>
        </div>
      )}

      <footer className="footer">
        <a href="/disclaimer">Disclaimer</a>
      </footer>

      {/* Vercel Analytics (tracks page views / usage) */}
      <Analytics />
    </div>
  );
}